import { Controller } from "@hotwired/stimulus"

// Remote modal — fetches an HTML modal fragment from `url` and injects it
// into <body>. The fragment carries its own `data-controller="modal"`
// wrapper (backdrop/escape close, scroll lock), so this controller only
// handles the fetch + a lightweight loading shell.
//
// Usage:
//   <button data-controller="remote-modal"
//           data-remote-modal-url-value="/some/fragment"
//           data-action="click->remote-modal#open">Reasons</button>
//
// First use: the admin "Reasons" item on recommendation cards
// (GET /autoboosts/:id/recommendations/reasons).
export default class extends Controller {
  static values = { url: String }

  connect() {
    console.log(`[remote-modal] connect url=${this.urlValue || "(none)"}`)
  }

  async open(event) {
    event?.preventDefault()
    if (!this.urlValue) {
      console.warn("[remote-modal] no url — refusing to open")
      return
    }

    const shell = this.#loadingShell()
    document.body.appendChild(shell)

    console.log(`[remote-modal] GET ${this.urlValue}`)
    try {
      const res = await fetch(this.urlValue, {
        method: "GET",
        headers: { Accept: "text/html" },
        credentials: "same-origin",
      })
      if (!res.ok) {
        console.error(`[remote-modal] ${res.status} on GET ${this.urlValue}`)
        shell.remove()
        return
      }

      const html = await res.text()
      shell.remove()
      // The fragment's own modal controller takes over on insertion.
      document.body.insertAdjacentHTML("beforeend", html)
      console.log(`[remote-modal] opened modal from ${this.urlValue}`)
    } catch (err) {
      console.error("[remote-modal] fetch failed", err)
      shell.remove()
    }
  }

  // Minimal backdrop + spinner while the fragment loads. Clicking the
  // backdrop cancels the visual shell (the in-flight fetch just no-ops
  // its shell.remove()).
  #loadingShell() {
    const el = document.createElement("div")
    el.className = "fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
    el.addEventListener("click", () => el.remove())
    el.innerHTML = `
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6">
        <svg class="w-6 h-6 animate-spin text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
        </svg>
      </div>`
    return el
  }
}
